// src/services/openIdToken.js
// Shared helper for obtaining a Matrix OpenID access_token.
//
// Preferred path: matrix-js-sdk v41 `client.getOpenIdToken()` (native helper).
// Fallback: raw POST to `/_matrix/client/v3/user/{userId}/openid/request_token`
// in case a future sdk upgrade changes `getOpenIdToken` semantics.
//
// Both paths honour the caller-provided `signal` and `timeoutMs`.
//
// The returned string is sensitive — callers MUST NOT log it or embed it in
// error messages. Network errors here are rethrown as-is; callers are
// responsible for sanitising their own error surfaces.

import { abortWithTimeout } from '../utils/abortWithTimeout';
import { timFetch } from '../utils/timUserAgent';

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * Obtain the Matrix OpenID access_token string for the current user.
 *
 * @param {object} client - matrix-js-sdk MatrixClient instance
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - optional external abort signal
 * @param {number} [options.timeoutMs=15000] - per-request timeout
 * @returns {Promise<string>} the access_token
 * @throws if the client is missing, the sdk call rejects, the raw fallback fails,
 *         or the merged abort signal fires before the token is returned
 */
export async function getMatrixOpenIdAccessToken(client, { signal, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  if (!client) throw new Error('Matrix client not ready');

  if (typeof client.getOpenIdToken === 'function') {
    return sdkOpenIdFetch(client, { signal, timeoutMs });
  }

  return rawOpenIdFetch(client, { signal, timeoutMs });
}

/**
 * Run `client.getOpenIdToken()` under a merged timeout + external-signal abort
 * race. The SDK method itself does not accept a signal, so we race its promise
 * against the merged controller's abort event to preserve the caller's
 * cancellation/timeout contract.
 * @private
 */
async function sdkOpenIdFetch(client, { signal, timeoutMs }) {
  const ctrl = abortWithTimeout(signal, timeoutMs);
  try {
    const abortPromise = new Promise((_resolve, reject) => {
      if (ctrl.signal.aborted) {
        reject(makeAbortError());
        return;
      }
      ctrl.signal.addEventListener('abort', () => reject(makeAbortError()), { once: true });
    });
    const tok = await Promise.race([client.getOpenIdToken(), abortPromise]);
    const accessToken = tok?.access_token;
    if (!accessToken) throw new Error('Empty OpenID token from sdk');
    return accessToken;
  } finally {
    ctrl._clearTimeout();
  }
}

/**
 * Raw-fetch fallback against the Matrix homeserver's openid/request_token
 * endpoint. Returns the access_token string.
 * @private
 */
async function rawOpenIdFetch(client, { signal, timeoutMs }) {
  const baseUrl = client.getHomeserverUrl();
  const userId = client.getUserId();
  const matrixAccessToken = client.getAccessToken();
  const ctrl = abortWithTimeout(signal, timeoutMs);

  try {
    const res = await timFetch(
      `${baseUrl}/_matrix/client/v3/user/${encodeURIComponent(userId)}/openid/request_token`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${matrixAccessToken}`,
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: '{}',
        signal: ctrl.signal,
      },
    );
    if (!res.ok) throw new Error(`openid/request_token HTTP ${res.status}`);
    const body = await res.json();
    if (!body?.access_token) throw new Error('Empty OpenID token from homeserver');
    return body.access_token;
  } finally {
    ctrl._clearTimeout();
  }
}

function makeAbortError() {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}
